import {
  ValidatedWhitelabelConfig,
  WhitelabelConfig,
  WhitelabelConfigResponse
} from './whitelabel-config.model';
import { WHITELABEL_OPTIONAL_DEFAULTS } from './whitelabel-config.defaults';

const COLOR_PATTERN =
  /^(#[0-9a-f]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\))$/i;
const FONT_PATTERN = /^[a-z0-9][a-z0-9 ,'"-]{0,79}$/i;

export class WhitelabelConfigValidationError extends Error {}

export function validateWhitelabelResponse(
  response: unknown,
  requestedDomain: string
): ValidatedWhitelabelConfig {
  if (!isRecord(response) || !isRecord(response.config_json)) {
    throw new WhitelabelConfigValidationError('The configuration response is invalid.');
  }

  const responseDomain = normalizeDomain(stringValue(response.domain));
  if (responseDomain && responseDomain !== requestedDomain) {
    throw new WhitelabelConfigValidationError('The configuration domain does not match.');
  }

  const raw = response.config_json as unknown as Partial<WhitelabelConfig>;
  const config = mergeConfig(raw);
  const siteDomain = normalizeDomain(config.site.domain);

  if (config.site.type !== 'operator') {
    throw new WhitelabelConfigValidationError('The domain is not configured as an operator.');
  }
  if (!config.site.name.trim() || !siteDomain) {
    throw new WhitelabelConfigValidationError('Operator name and domain are required.');
  }
  if (siteDomain !== requestedDomain) {
    throw new WhitelabelConfigValidationError('The operator domain does not match.');
  }

  const partnerOperators = normalizeIds(config.partnerOperators);
  const routeOperatorIds = normalizeIds(config.routeConfig.operatorIds);
  if (
    partnerOperators.length &&
    routeOperatorIds.length &&
    partnerOperators.join(',') !== routeOperatorIds.join(',')
  ) {
    throw new WhitelabelConfigValidationError(
      'partnerOperators and routeConfig.operatorIds must match.'
    );
  }
  const operatorIds = routeOperatorIds.length ? routeOperatorIds : partnerOperators;
  if (!operatorIds.length) {
    throw new WhitelabelConfigValidationError(
      'At least one operator ID is required. Empty is never unrestricted.'
    );
  }

  validateTheme(config);

  return {
    ...config,
    domain: requestedDomain,
    partnerOperators: operatorIds,
    routeConfig: { ...config.routeConfig, operatorIds },
    operatorIds
  };
}

function mergeConfig(raw: Partial<WhitelabelConfig>): WhitelabelConfig {
  return {
    ...WHITELABEL_OPTIONAL_DEFAULTS,
    ...raw,
    site: {
      ...WHITELABEL_OPTIONAL_DEFAULTS.site,
      ...(raw.site || {}),
      alternateNames: arrayValue<unknown>(raw.site?.alternateNames)
        .map(item => String(item).trim())
        .filter(Boolean)
    },
    self: {
      ...WHITELABEL_OPTIONAL_DEFAULTS.self,
      ...(raw.self || {}),
      bannerGradient: booleanValue(
        raw.self && raw.self.bannerGradient,
        WHITELABEL_OPTIONAL_DEFAULTS.self.bannerGradient
      )
    },
    colors: { ...WHITELABEL_OPTIONAL_DEFAULTS.colors, ...(raw.colors || {}) },
    fonts: { ...WHITELABEL_OPTIONAL_DEFAULTS.fonts, ...(raw.fonts || {}) },
    texts: { ...WHITELABEL_OPTIONAL_DEFAULTS.texts, ...(raw.texts || {}) },
    routeConfig: {
      ...WHITELABEL_OPTIONAL_DEFAULTS.routeConfig,
      ...(raw.routeConfig || {}),
      altRoutes: {
        ...WHITELABEL_OPTIONAL_DEFAULTS.routeConfig.altRoutes,
        ...((raw.routeConfig && raw.routeConfig.altRoutes) || {})
      }
    },
    socialLinks: {
      ...WHITELABEL_OPTIONAL_DEFAULTS.socialLinks,
      ...(raw.socialLinks || {})
    },
    partnerOperators: arrayValue(raw.partnerOperators),
    paymentOptions: arrayValue(raw.paymentOptions),
    slides: arrayValue(raw.slides),
    operatingCountries: arrayValue(raw.operatingCountries),
    passengerValidation: {
      ...WHITELABEL_OPTIONAL_DEFAULTS.passengerValidation,
      ...(raw.passengerValidation || {}),
      documentTypes: arrayValue(raw.passengerValidation?.documentTypes).length
        ? arrayValue(raw.passengerValidation?.documentTypes)
        : WHITELABEL_OPTIONAL_DEFAULTS.passengerValidation.documentTypes,
      phoneCountries: arrayValue(raw.passengerValidation?.phoneCountries).length
        ? arrayValue(raw.passengerValidation?.phoneCountries)
        : WHITELABEL_OPTIONAL_DEFAULTS.passengerValidation.phoneCountries
    },
    content: raw.content || {},
    seo: raw.seo || {}
  };
}

function validateTheme(config: WhitelabelConfig): void {
  Object.keys(config.colors).forEach(key => {
    const value = config.colors[key as keyof typeof config.colors];
    if (!COLOR_PATTERN.test(value)) {
      throw new WhitelabelConfigValidationError(`Invalid theme color: ${key}.`);
    }
  });

  [config.fonts.body, config.fonts.title].forEach(font => {
    if (!FONT_PATTERN.test(font)) {
      throw new WhitelabelConfigValidationError('Invalid configured font name.');
    }
  });

  [
    config.self.logo,
    config.self.logoLight,
    config.self.favIcon,
    config.site.canonicalUrl,
    config.site.marketplaceUrl,
    config.site.appStoreUrl,
    config.site.iosAppStoreUrl,
    config.site.privacyPolicyUrl,
    config.site.termsUrl,
    ...Object.values(config.socialLinks),
    ...config.slides.flatMap(slide => [slide.image, slide.link]),
    ...contentAssetUrls(config.content)
  ].filter((value): value is string => !!value).forEach(assertSafeHttpUrl);
}

function contentAssetUrls(value: unknown, key = ''): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(item => contentAssetUrls(item, key));
  }
  if (isRecord(value)) {
    return Object.entries(value).flatMap(([childKey, childValue]) =>
      contentAssetUrls(childValue, childKey)
    );
  }
  return typeof value === 'string' && /(image|url)$/i.test(key) ? [value] : [];
}

function assertSafeHttpUrl(value: string): void {
  if (value.startsWith('/') && !value.startsWith('//')) {
    return;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error();
    }
  } catch {
    throw new WhitelabelConfigValidationError('An unsafe configured URL was rejected.');
  }
}

function normalizeIds(value: unknown): string[] {
  return Array.from(
    new Set(arrayValue<unknown>(value).map(item => String(item).trim()).filter(Boolean))
  ).sort();
}

function normalizeDomain(value: string): string {
  return value.trim().toLowerCase().replace(/^www\./, '').replace(/:\d+$/, '');
}

function arrayValue<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
